/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import metrics from '@adobe/exc-app/metrics';
import {query as queryApi} from '@adobe/exc-app/network';

const GQL_FRAGMENTS = {
  imsSSOhandler: `
    fragment imsSSOhandlerFragment on ImsSSOhandlerResponse {
      message
      currentImsOrgId
      currentAnalyticsPC
    }`,
  userOrgs: `
    fragment userOrgsFragment on ImsOrganization {
      orgName
      orgRef {
        ident
        authSrc
      }
      countryCode
      orgType
    }`,
  userOrgSettings: `
    fragment userOrgSettingsFragment on CustomPreferencesResponse {
      level
      appId
      groupId
      userId
      orgId
      settings
    }`};

export const RELOAD_QUERY = `
  query shellInitDataQuery($sessionUrl: String) {
    userOrgs {
      ...userOrgsFragment
    }
    imsSessionData(sessionUrl: $sessionUrl)
  }
  ${GQL_FRAGMENTS.userOrgs}
`;

/* eslint-disable max-len */
export const LOGIN_QUERY = `
  query shellInitDataQuery($pathTenantId: String) {
    userOrgs {
      ...userOrgsFragment
    }
    imsSSOhandler(prefixTenantId: $pathTenantId) {
      ...imsSSOhandlerFragment
    }
    userProfileJson
  }
  ${GQL_FRAGMENTS.userOrgs}
  ${GQL_FRAGMENTS.imsSSOhandler}
`;

export const USER_DATA_QUERY = `
  query additionalUserData($imageKeySize: [Int]!) {
    userBehance {
      images
      encodedImage(imageKey: $imageKeySize)
    }
    imsAccountClusterData(visibleOnly: "true", size: "1000") {
      data {
        userId
        userType
        description
        status
        default
        visible
      }
      next
    }
  }
`;

export const SHELL_HEADER_QUERY = `
  query shellHeaderQuery($locale: String, $config_version: Int, $keys: [String]!) {
    userOrgSettings(keys: $keys, appId: "pulse", groupId: "notifications") {
      ...userOrgSettingsFragment
    }
    userOrgPreferences {
      displayAlert
    }
    timeline(config_version: $config_version, locale: $locale)
    userProfileJson
  }
  ${GQL_FRAGMENTS.userOrgSettings}
`;

export const SANDBOX_QUERY = `
  query getUserSandboxes($fulfillableData: FulfillableData) {
    userSandboxes(fulfillableData: $fulfillableData) {
      title
      region
      name
      state
      isDefault
      type
    }
  }
`;

export const LAUNCH_DARKLY_QUERY = `
  query imsOrgFeatureFlagSettingsQuery {
    imsOrgFeatureFlagSettings(projectID: "Unified Shell") {
      featureFlags {
        name
        value
      }
    }
  }
`;
/* eslint-enable max-len */

const REQ_ERR_MSG = '[GraphQL Service Error in Shell Data Init]';

export class GraphQlService {
  constructor() {
    this.metrics = metrics.create('exc.core.GraphQlService');
  }

  initializeShellDataForLogin(variables) {
    return this.shellDataHandler(LOGIN_QUERY, {
      debugKey: 'User Login'
    })(variables);
  }

  initializeShellDataForReload(variables) {
    return this.shellDataHandler(RELOAD_QUERY, {
      debugKey: 'App Re-load'
    })(variables);
  }

  initializeShellDataForHeader(variables, getProfile) {
    // If we don't need a fresh profile, remove it from the query.
    const query = getProfile ?
      SHELL_HEADER_QUERY :
      SHELL_HEADER_QUERY.replace(/.*userProfileJson.*\n/, '');
    return this.shellDataHandler(query, {
      debugKey: 'Header Data',
      queryName: 'HeaderData'
    })(variables);
  }

  getAdditionalUserData(variables) {
    return this.shellDataHandler(USER_DATA_QUERY, {
      debugKey: 'Account Cluster',
      queryName: 'AccountCluster'
    })(variables);
  }

  async getUserSandboxes(variables) {
    return this.shellDataHandler(SANDBOX_QUERY, {
      debugKey: 'User Sandboxes',
      queryName: 'UserSandboxes'
    })(variables);
  }

  launchDarklyFeatureFlags() {
    return this.shellDataHandler(LAUNCH_DARKLY_QUERY, {
      debugKey: 'Launch Darkly',
      queryName: 'imsOrgFeatureFlagSettings'
    })();
  }

  async getGqlResponse(res) {
    if (res && !res.ok) {
      let reqId = res && res.headers && res.headers.get('x-request-id');
      let e = new Error(`Non-2xx response from gql. ReqID: ${reqId}`);
      e.reqId = reqId;
      e.status = res.status;
      throw e;
    }

    const json = await res.json() || {};
    json.reqId = res && res.headers && res.headers.get('x-request-id');

    // Handling inconsistent 401s from the gql response layer
    // If we get > 1, we want to throw an error. This is due to adobe.io caching.
    const errors = {};
    json.errors && json.errors.forEach(err => {
      const status = ((err.extensions || {}).response || {}).status;
      errors[status] = errors[status] || [];
      errors[status].push(err.path.join('.'));
      this.metrics.error(`${REQ_ERR_MSG} Message: ${err.message}`, err, {requestId: json.reqId});
    });

    if (Object.keys(errors).length) {
      let e = new Error(`Non-2xx response from backing services in gql. ReqID: ${json.reqId}`);
      e.errors = errors;
      throw e;
    }

    return json;
  }

  async request(query, variables) {
    let res = await queryApi({data: {query, variables}, operationName: this.getOperationName(query)});
    return await this.getGqlResponse(res);
  }

  shellDataHandler(query, {debugKey, queryName = 'GraphQl'}) {
    return async variables => {
      const gqlTimer = this.metrics.start(queryName);
      try {
        const response = await this.request(query, variables || {});
        const data = response.data;
        if (data) {
          gqlTimer.time('data.initialized', `Initialized Shell Data from GQL required for ${debugKey}.`, {requestId: response.reqID});
          this.metrics.info(`Initialized Shell Data required for ${debugKey} using GQL.`,
            {requestId: response.reqID});
        } else {
          this.metrics.error(`GQL returned invalid/null response for ${debugKey} Data`);
        }
        return data;
      } catch (error) {
        gqlTimer.time('failure', `Failed to initialize Shell App Data for ${debugKey} using GQL. ReqID: ${error.reqId}`);
        this.metrics.error(`Failed to initialize Shell App Data for ${debugKey}. ReqID: ${error.reqId}`);
        throw error;
      }
    };
  }

  getOperationName(query) {
    // Extract operation name from query
    const name = /(query|mutation) ?(\w+)/.exec(query);
    return (name && name.length) ? name[2] : undefined;
  }
}
